/** @format */
import React, {useState, useEffect, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import Moment from 'moment';
import {
    parseSearchQueryByKey,
    updateSearchQueryByKey,
    getFilters
} from '../../utilities/search_query_parse';
import {SearchContext} from 'frontier/components/search/index';

export const useSearch = ({
    action = '',
    searchKey = 'defaultSearchKey',
    modified = 0,
    navigate,
    history
}) => {
    const initialQuery = parseSearchQueryByKey(searchKey);
    const {search: initialSearch, sort, direction, filters, page: queryPage} = initialQuery;

    const [state, setState] = useState({
        listItems: [],
        extraData: {},
        search: initialSearch,
        filters: filters,
        error: false,
        pending: false,
        sort: sort,
        direction: direction,
        page: typeof queryPage !== 'undefined' ? queryPage : 1,
        nextPage: false,
        lastModified: false,
        showResults: false,
        paginationSize: 5
    });

    const containerRef = useRef(null);
    const abortController = useRef(null);
    const searchTimeout = useRef(null);

    // Helper to compare filters in the same way as the class version did.
    const areFiltersEqual = useCallback((a, b) => {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (
                a[i].query.toString() !== b[i].query.toString() ||
                a[i].value.toString() !== b[i].value.toString()
            ) {
                return false;
            }
        }
        return true;
    }, []);

    const getSearchString = useCallback(({filters, search}) => {
        const filterMap = filters.map(filter => `${filter.query} ${filter.value}`);
        return `${search} ${filterMap.join(' ')}`.trim();
    }, []);

    const onSearchComplete = useCallback(
        (data, skipUpdateHistory, replaceHistory = false) => {
            setState(prevState => {
                let listItems = [];
                let extraData = {};
                let nextPage = false;

                if (typeof data === 'object' && typeof data.pagination === 'object') {
                    if (data.pagination.page > 1) {
                        listItems = [...prevState.listItems, ...data.data];
                        extraData = data.extraData;
                    } else {
                        listItems = data.data;
                        extraData = data.extraData;
                    }
                    nextPage = data.pagination.nextPage;
                } else if (typeof data === 'object' && data.data) {
                    listItems = data.data;
                    extraData = data.extraData;
                }

                // Update the URL if needed.
                if (!skipUpdateHistory) {
                    const searchQuery = updateSearchQueryByKey(searchKey, data.query);
                    if (replaceHistory && history) {
                        history.replace({search: `?${searchQuery}`});
                    } else if (navigate) {
                        navigate({search: `?${searchQuery}`});
                    }
                }

                return {
                    ...prevState,
                    pending: false,
                    error: false,
                    lastModified: Moment().unix(),
                    nextPage,
                    listItems,
                    extraData
                };
            });
        },
        [searchKey, navigate, history]
    );

    const executeSearch = useCallback(
        async (currentState, skipUpdateHistory = false, replaceHistory = false) => {
            try {
                const {page, search, filters, sort, direction, paginationSize} = currentState;
                const query = {
                    search: getSearchString({filters, search}),
                    page: page || 1,
                    paginationSize
                };

                if (sort) {
                    query.sort = sort;
                    query.direction = direction;
                }

                const response = await fetch(`${action}?${new URLSearchParams(query)}`, {
                    signal: abortController.current.signal,
                    method: 'GET',
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json'
                    }
                });

                const data = await response.json();
                data.query = query;
                onSearchComplete(data, skipUpdateHistory, replaceHistory);
            } catch (e) {
                if (e.name !== 'AbortError') {
                    setState(currentState => ({
                        ...currentState,
                        pending: false,
                        error: e.message
                    }));
                }
            }
        },
        [action, getSearchString, onSearchComplete]
    );

    // internalSearch consolidates the "set state and then trigger search" logic.
    const internalSearch = useCallback(
        (updateTarget, skipUpdateHistory = false, replaceHistory = false) => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
            if (abortController.current) {
                abortController.current.abort('New Request, Cancel Outdated Request');
            }
            abortController.current = new AbortController();

            setState(prevState => {
                const newState = update(prevState, {
                    ...updateTarget,
                    pending: {$set: true}
                });

                searchTimeout.current = setTimeout(() => {
                    executeSearch(newState, skipUpdateHistory, replaceHistory);
                }, 500);

                return newState;
            });
        },
        [executeSearch]
    );

    // Expose onSearch as an alias to internalSearch.
    const onSearch = internalSearch;

    // Refactored action: Add a filter.
    const onAddFilter = useCallback(
        filter => {
            const currentState = state;
            const existingFilterIndex = currentState.filters.findIndex(
                existingFilter => existingFilter.query === filter.query
            );
            let updateTarget;
            if (existingFilterIndex > -1) {
                updateTarget = {
                    filters: {$splice: [[existingFilterIndex, 1, filter]]},
                    page: {$set: 1}
                };
            } else {
                updateTarget = {filters: {$push: [filter]}, page: {$set: 1}};
            }
            internalSearch(updateTarget);
        },
        [state, internalSearch]
    );

    const onAddFilterQuery = useCallback(
        filterQuery => {
            const filters = getFilters(filterQuery);
            filters.forEach(filter => {
                onAddFilter(filter);
            });
        },
        [getFilters, onAddFilter]
    );

    const onRemoveFilter = useCallback(
        filterQuery => {
            const currentState = state;
            const filterIndex = currentState.filters.findIndex(({query}) => query === filterQuery);
            if (filterIndex === -1) return;
            const updateTarget = {filters: {$splice: [[filterIndex, 1]]}, page: {$set: 1}};
            internalSearch(updateTarget);
        },
        [state, internalSearch]
    );

    const onReset = useCallback(() => {
        internalSearch({
            search: {$set: ''},
            filters: {$set: []},
            page: {$set: 1},
            sort: {$set: null},
            direction: {$set: null}
        });
    }, [internalSearch]);

    const onSort = useCallback(
        (sortValue, directionValue) => {
            internalSearch({
                page: {$set: 1},
                sort: {$set: sortValue},
                direction: {$set: directionValue}
            });
        },
        [internalSearch]
    );

    const onHideResults = useCallback(() => {
        if (!containerRef.current) return;
        setTimeout(() => {
            if (
                containerRef.current !== document.activeElement &&
                !containerRef.current.contains(document.activeElement)
            ) {
                setState(prevState => ({...prevState, showResults: false}));
            }
        }, 0);
    }, []);

    const onShowResults = useCallback(() => {
        setState(prevState => ({...prevState, showResults: true}));
    }, []);

    const isSearchActive = useCallback(() => {
        return state.search !== '' || state.filters.length > 0;
    }, [state.search, state.filters]);

    const handleRemoveItemFromList = useCallback(
        (id, shouldFetchAfterRemove) => {
            const currentState = state;
            const newListItems = currentState.listItems.filter(item => item.id !== id);
            setState({...currentState, listItems: newListItems});
            if (shouldFetchAfterRemove && newListItems.length < 5) {
                internalSearch({
                    search: {$set: ''},
                    filters: {$set: []},
                    page: {$set: 1},
                    sort: {$set: null},
                    direction: {$set: null}
                });
            }
        },
        [state, internalSearch]
    );

    const handleChangePaginationSize = useCallback(
        size => {
            internalSearch({paginationSize: {$set: size}});
        },
        [internalSearch]
    );

    // --- URL sync effect ---
    // Only trigger an update from the URL (e.g. when using the back/forward button)
    // if the external values have changed. We now only depend on searchKey and modified,
    // ensuring that internal updates (like sorting) aren’t immediately overwritten.
    useEffect(() => {
        const urlQuery = parseSearchQueryByKey(searchKey);
        if (
            urlQuery.search !== state.search ||
            urlQuery.sort !== state.sort ||
            urlQuery.direction !== state.direction ||
            !areFiltersEqual(urlQuery.filters, state.filters)
        ) {
            internalSearch(
                {
                    search: {$set: urlQuery.search},
                    filters: {$set: urlQuery.filters},
                    sort: {$set: urlQuery.sort},
                    direction: {$set: urlQuery.direction},
                    page: {$set: 1}
                },
                true
            );
        }
    }, [searchKey, modified]); // Notice state is no longer in the dependency array

    // Cleanup pending timeouts and abort any in-flight requests when unmounting.
    useEffect(() => {
        return () => {
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }
            if (abortController.current) {
                abortController.current.abort();
            }
        };
    }, []);

    return {
        ...state,
        containerRef,
        onUpdate: newState => setState(prevState => update(prevState, newState)),
        onSearch,
        onAddFilter,
        onAddFilterQuery,
        onRemoveFilter,
        onReset,
        onSort,
        onHideResults,
        onShowResults,
        handleRemoveItemFromList,
        handleChangePaginationSize,
        isSearchActive
    };
};

const withSearch = WrappedComponent => {
    const WithSearchHandler = props => {
        const searchProps = useSearch(props);
        return (
            <SearchContext.Provider value={{...props, ...searchProps}}>
                <WrappedComponent {...props} {...searchProps} />
            </SearchContext.Provider>
        );
    };

    WithSearchHandler.defaultProps = {
        action: '',
        search: '',
        sort: null,
        direction: null,
        filters: [],
        modified: 0,
        searchKey: 'defaultSearchKey'
    };

    WithSearchHandler.propTypes = {
        modified: PropTypes.number.isRequired
    };

    return WithSearchHandler;
};

export default withSearch;
